import { z } from "zod/v4";
import { AnnouncementErrorBody, type AnnouncementErrorReason } from "../../client-messages/announcements";

/**
 * How the page reaches the root announcements API: the same pair the
 * `@visioni/a3s-dir/app` hooks take (root API base URL and the app's
 * authenticated fetch).
 */
export interface AnnouncementsApiConfig {
  authenticatedFetch: (url: string, init?: RequestInit) => Promise<Response>;
  directoryBaseUrl: string;
}

/** A refused request. `reason` is absent only when the API did not answer with one. */
export class AnnouncementsApiError extends Error {
  readonly status: number;
  readonly reason: AnnouncementErrorReason | undefined;
  readonly body: AnnouncementErrorBody | undefined;

  constructor(status: number, body: AnnouncementErrorBody | undefined) {
    super(
      body?.message ??
        (status === 404 ? "Announcements are not available in this environment."
        : status === 413 ? "The file is too large to upload."
        : `Something went wrong (${String(status)}). It may not have finished; check before trying again.`),
    );
    this.name = "AnnouncementsApiError";
    this.status = status;
    this.reason = body?.reason;
    this.body = body;
  }

  /** The API is not deployed here (MR environments have no root secret). */
  get notAvailable(): boolean {
    return this.status === 404 && this.reason === undefined;
  }
}

export async function announcementsRequest<T extends z.ZodType>(
  config: AnnouncementsApiConfig,
  request: {
    method: "GET" | "POST";
    path: string;
    /** Sent as JSON. */
    body?: unknown;
    /** Sent as is, e.g. an uploaded CSV file. */
    file?: { contentType: string; text: string };
    signal?: AbortSignal;
  },
  responseSchema: T,
): Promise<z.output<T>> {
  const response = await config.authenticatedFetch(`${config.directoryBaseUrl}${request.path}`, {
    method: request.method,
    ...(request.signal && { signal: request.signal }),
    ...(request.body !== undefined && {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request.body),
    }),
    ...(request.file !== undefined && {
      headers: { "Content-Type": request.file.contentType },
      body: request.file.text,
    }),
  });
  const json: unknown = await response.json().catch(() => undefined);
  if (!response.ok) {
    const body = AnnouncementErrorBody.safeParse(json);
    throw new AnnouncementsApiError(response.status, body.success ? body.data : undefined);
  }
  return responseSchema.parse(json);
}
